import React, { memo } from 'react';
import { Handle, Position, NodeProps } from 'reactflow';
import { Box, Typography, IconButton, Chip, useTheme } from '@mui/material';
import {
  Pencil,
  Trash2,
  Type,
  List,
  ToggleLeft,
  Mail,
  Phone,
  Hash,
  AlignLeft,
  GripVertical,
  Sparkles,
  SlidersHorizontal,
  Calendar,
  Clock,
  Globe,
  ShieldCheck,
  Paperclip,
  Link2,
} from 'lucide-react';
import { FormField, FormFieldNodeData } from '../../types/form';

const getFieldTypeIcon = (field: FormField) => {
  if (field.fieldType === 'number' && field.rangeType) {
    return <SlidersHorizontal size={14} />;
  }
  const icons: Record<string, React.ReactNode> = {
    text: <Type size={14} />,
    textarea: <AlignLeft size={14} />,
    select: <List size={14} />,
    boolean: <ToggleLeft size={14} />,
    email: <Mail size={14} />,
    phone: <Phone size={14} />,
    number: <Hash size={14} />,
    ai: <Sparkles size={14} />,
    date: <Calendar size={14} />,
    time: <Clock size={14} />,
    http_request: <Globe size={14} />,
    customer_verify: <ShieldCheck size={14} />,
    file: <Paperclip size={14} />,
    url: <Link2 size={14} />,
  };
  return icons[field.fieldType] || <Type size={14} />;
};

const getFieldTypeName = (field: FormField) => {
  if (field.fieldType === 'number' && field.rangeType) {
    return 'Range';
  }
  const names: Record<string, string> = {
    text: 'Text',
    textarea: 'Text Area',
    select: 'Dropdown',
    boolean: 'Yes/No',
    number: 'Number',
    email: 'Email',
    phone: 'Phone',
    ai: 'AI Prompt',
    date: 'Date Picker',
    time: 'Time Picker',
    http_request: 'HTTP Request',
    customer_verify: 'Verify Customer',
    file: 'File Upload',
    url: 'URL',
  };
  return names[field.fieldType] || field.fieldType;
};

const FormFieldNode: React.FC<NodeProps<FormFieldNodeData>> = ({ data }) => {
  const theme = useTheme();
  const { field, onEdit, onDelete } = data;

  return (
    <Box
      sx={{
        backgroundColor: theme.palette.background.paper,
        border: `1px solid ${theme.palette.divider}`,
        borderRadius: 2,
        minWidth: 300,
        maxWidth: 400,
        boxShadow: theme.shadows[1],
        '&:hover': {
          borderColor: theme.palette.primary.main,
          boxShadow: theme.shadows[3],
        },
      }}
    >
      <Handle
        type="target"
        position={Position.Top}
        style={{
          background: theme.palette.primary.main,
          width: 8,
          height: 8,
        }}
      />

      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1,
          borderBottom: `1px solid ${theme.palette.divider}`,
          backgroundColor: `${theme.palette.primary.main}08`,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <GripVertical size={14} color={theme.palette.text.secondary} style={{ cursor: 'grab' }} />
          {getFieldTypeIcon(field)}
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            {getFieldTypeName(field)}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            #{field.order}
          </Typography>
        </Box>
        <Box>
          <IconButton size="small" onClick={() => onEdit(field.fieldId)} sx={{ mr: 0.5 }}>
            <Pencil size={14} />
          </IconButton>
          <IconButton size="small" color="error" onClick={() => onDelete(field.fieldId)}>
            <Trash2 size={14} />
          </IconButton>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ px: 2, py: 1.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 500, mb: 0.5 }}>
          {field.label || 'Untitled Field'}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexWrap: 'wrap' }}>
          <Chip
            label={field.fieldId}
            size="small"
            variant="outlined"
            sx={{ fontSize: '0.7rem', height: 20 }}
          />
          {field.required && (
            <Chip
              label="Required"
              size="small"
              color="error"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {field.validation && (
            <Chip
              label={field.validation.type}
              size="small"
              color="info"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {field.rangeType && (
            <Chip
              label={`Range: ${field.rangeType.min} - ${field.rangeType.max}`}
              size="small"
              color="warning"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
          {field.conditional && (
            <Chip
              label={`If ${field.conditional.dependsOn}`}
              size="small"
              color="secondary"
              sx={{ fontSize: '0.7rem', height: 20 }}
            />
          )}
        </Box>

        {field.fieldType === 'select' && field.options && field.options.length > 0 && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
            {field.options.length} option{field.options.length !== 1 ? 's' : ''}
          </Typography>
        )}

        {field.placeholder && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
            Placeholder: {field.placeholder}
          </Typography>
        )}
      </Box>

      <Handle
        type="source"
        position={Position.Bottom}
        style={{
          background: theme.palette.primary.main,
          width: 8,
          height: 8,
        }}
      />
    </Box>
  );
};

export default memo(FormFieldNode);
